/**
 * Admin Accounts Page Component
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../store/AuthContext';
import { apiService } from '../services/apiService';
import AddAdminModal from '../components/modals/AddAdminModal';
import EditAdminModal from '../components/modals/EditAdminModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import './AdminAccountsPage.css';

interface AdminListItem {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  status?: 'pending_activation' | 'active' | 'inactive';
  created_at: string;
  last_login_at: string | null;
}

const AdminAccountsPage: React.FC = () => {
  const { admin } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    admin: AdminListItem | null;
  }>({
    isOpen: false,
    admin: null,
  });
  const [admins, setAdmins] = useState<AdminListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    adminId: string | null;
    adminName: string;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    adminId: null,
    adminName: '',
    action: 'disable',
  });

  // Only master admin can see and add other admins
  const isMasterAdmin = admin?.role === 'master';

  useEffect(() => {
    if (isMasterAdmin) {
      loadAdmins();
    }
  }, [isMasterAdmin]);

  const loadAdmins = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllAdmins();
      setAdmins(response.admins);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSuccess = () => {
    loadAdmins();
  };

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      master: 'Master Admin',
      risk: 'Risk & AML Admin',
      finance: 'Finance Admin',
      support: 'Support Admin',
      compliance: 'Compliance Admin',
      content: 'Content Admin',
    };
    return roleMap[role] || role;
  };

  const handleDisableClick = (adminItem: AdminListItem) => {
    if (adminItem.role === 'master') {
      setError('Master Admin accounts cannot be disabled');
      return;
    }
    setConfirmModal({
      isOpen: true,
      adminId: adminItem.id,
      adminName: adminItem.name,
      action: 'disable',
    });
  };

  const handleEnableClick = (adminItem: AdminListItem) => {
    setConfirmModal({
      isOpen: true,
      adminId: adminItem.id,
      adminName: adminItem.name,
      action: 'enable',
    });
  };

  const handleConfirmStatusChange = async () => {
    if (!confirmModal.adminId) return;

    setIsLoading(true);
    setError(null);
    try {
      await apiService.updateAdminStatus(
        confirmModal.adminId,
        confirmModal.action === 'enable'
      );
      setConfirmModal({ isOpen: false, adminId: null, adminName: '', action: 'disable' });
      await loadAdmins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isMasterAdmin) {
    return (
      <div className="admin-accounts-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Admin Accounts</h1>
            <p className="page-sub">Role-based access control (RBAC)</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)' }}>Access denied. Only Master Admin can view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-accounts-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Admin Accounts</h1>
          <p className="page-sub">Role-based access control (RBAC)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setIsAddModalOpen(true)}>
          ➕ Add Admin
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)' }}>Loading admin accounts...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="card-body">
            <p style={{ color: 'var(--text-muted)' }}>No admin accounts found.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Admin</th>
                <th>Email</th>
                <th>Role</th>
                <th>MFA</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((adminItem) => {
                const initials = adminItem.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);

                const formatLastLogin = () => {
                  if (!adminItem.last_login_at) return 'Never';
                  const date = new Date(adminItem.last_login_at);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);

                  if (diffMins < 60) return `${diffMins}m ago`;
                  if (diffHours < 24) return `${diffHours}h ago`;
                  if (diffDays < 7) return `${diffDays}d ago`;
                  return date.toLocaleDateString();
                };

                return (
                  <tr key={adminItem.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-av" style={{ background: 'var(--purple)' }}>
                          {initials}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{adminItem.name}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '12px' }}>{adminItem.email}</td>
                    <td>
                      <span className="status blue">{getRoleDisplayName(adminItem.role)}</span>
                    </td>
                    <td>
                      <span className="status green">✓</span>
                    </td>
                    <td>
                      <span className={`status ${
                        adminItem.status === 'inactive' ? 'red' : 
                        adminItem.status === 'pending_activation' ? 'yellow' : 
                        'green'
                      }`}>
                        {adminItem.status === 'pending_activation' ? 'Pending activation' : 
                         adminItem.status === 'inactive' ? 'inactive' : 
                         'active'}
                      </span>
                    </td>
                    <td>{formatLastLogin()}</td>
                    <td>
                      <button 
                        className="action" 
                        onClick={() => setEditModal({ isOpen: true, admin: adminItem })}
                      >
                        Edit
                      </button>
                      {adminItem.active ? (
                        <button
                          className="action red"
                          onClick={() => handleDisableClick(adminItem)}
                          disabled={adminItem.role === 'master'}
                        >
                          Disable
                        </button>
                      ) : (
                        <button
                          className="action"
                          onClick={() => handleEnableClick(adminItem)}
                        >
                          Enable
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AddAdminModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <EditAdminModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, admin: null })}
        onSuccess={handleAddSuccess}
        admin={editModal.admin ? {
          id: editModal.admin.id,
          email: editModal.admin.email,
          name: editModal.admin.name,
          role: editModal.admin.role,
        } : null}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.action === 'disable' ? 'Disable Admin Account' : 'Enable Admin Account'}
        message={
          confirmModal.action === 'disable'
            ? `Are you sure you want to disable "${confirmModal.adminName}"? They will not be able to log in until their account is re-enabled.`
            : `Are you sure you want to enable "${confirmModal.adminName}"? They will be able to log in again.`
        }
        confirmText={confirmModal.action === 'disable' ? 'Disable' : 'Enable'}
        confirmButtonClass={confirmModal.action === 'disable' ? 'danger' : 'primary'}
        onConfirm={handleConfirmStatusChange}
        onCancel={() => setConfirmModal({ isOpen: false, adminId: null, adminName: '', action: 'disable' })}
      />
    </div>
  );
};

export default AdminAccountsPage;

